import { createFileRoute, notFound, useNavigate } from '@tanstack/react-router';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PlayerGroupDashboard } from '@/components/features/player-groups/PlayerGroupDashboard';
import { PlayerGroupFormDialog } from '@/components/features/player-groups/PlayerGroupFormDialog';
import { Button } from '@/components/ui/button';
import {
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogDescription,
  ResponsiveDialogFooter,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
} from '@/components/ui/responsive-dialog';
import { deletePlayerGroup, restorePlayerGroup } from '@/db/player-groups';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { usePlayerGroup } from '@/hooks/usePlayerGroup';
import { usePlayers } from '@/hooks/usePlayers';
import { handleUserFacingError } from '@/lib/error-handler';
import { toastDeleteWithUndo, toastError } from '@/lib/toast-helpers';
import { parsePlayerGroupId, safeParsePlayerGroupId, type PlayerGroup } from '@/schemas';

export const Route = createFileRoute('/player-groups/$groupId')({
  loader: ({ params }) => {
    const parseResult = safeParsePlayerGroupId(params.groupId);
    if (!parseResult.success) {
      // eslint-disable-next-line @typescript-eslint/only-throw-error -- notFound() is TanStack Router's official API for 404 handling, not a standard Error
      throw notFound();
    }
  },
  component: PlayerGroupDetailRoute,
});

function PlayerGroupDetailRoute() {
  const { groupId: groupIdParam } = Route.useParams();
  const navigate = useNavigate();
  const { t } = useTranslation('common');

  const groupId = parsePlayerGroupId(groupIdParam);
  const { playerGroup, isLoading, error } = usePlayerGroup(groupId);
  const { players, refresh: refreshPlayers } = usePlayers({ includeDeleted: false });

  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const documentTitle = useMemo(() => {
    const groupName = playerGroup?.name || t('playerGroups.unnamed', 'Unnamed Group');
    return `${groupName} - ${t('playerGroups.detail.title', 'Player group')} · ${t(
      'app.name',
      'One Score',
    )}`;
  }, [playerGroup?.name, t]);

  useDocumentTitle(documentTitle);

  const handleEditClick = useCallback((_group: PlayerGroup) => {
    setShowEditDialog(true);
  }, []);

  const handleDeleteClick = useCallback((_group: PlayerGroup) => {
    setShowDeleteDialog(true);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!playerGroup) return;

    setIsDeleting(true);
    try {
      await deletePlayerGroup(playerGroup.id);
      toastDeleteWithUndo(
        t('playerGroups.deleteSuccess', 'Player group deleted successfully'),
        async () => {
          await restorePlayerGroup(playerGroup.id);
        },
        t('undo', 'undo'),
      );
      void navigate({ to: '/player-groups' });
    } catch (error: unknown) {
      const errorMessage = handleUserFacingError(
        error,
        t('playerGroups.deleteError', 'Failed to delete player group'),
        { operation: 'deletePlayerGroup', groupId: playerGroup.id },
      );
      toastError(errorMessage);
      setIsDeleting(false);
    }
  }, [playerGroup, t, navigate]);

  const handleDeleteCancel = useCallback(() => {
    setShowDeleteDialog(false);
  }, []);

  const handleFormSuccess = useCallback(() => {
    void refreshPlayers();
  }, [refreshPlayers]);

  return (
    <div className="mx-auto w-full max-w-5xl space-y-8 px-4 py-6">
      <PlayerGroupDashboard
        playerGroup={playerGroup}
        players={players}
        isLoading={isLoading}
        error={error}
        onEdit={playerGroup ? handleEditClick : undefined}
        onDelete={playerGroup ? handleDeleteClick : undefined}
      />

      {/* Edit Dialog */}
      {playerGroup && (
        <PlayerGroupFormDialog
          open={showEditDialog}
          onOpenChange={setShowEditDialog}
          players={players}
          group={playerGroup}
          onSuccess={handleFormSuccess}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <ResponsiveDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <ResponsiveDialogContent className="sm:max-w-lg">
          <ResponsiveDialogHeader>
            <ResponsiveDialogTitle>
              {t('playerGroups.deleteConfirm.title', 'Delete player group?')}
            </ResponsiveDialogTitle>
            <ResponsiveDialogDescription>
              {t(
                'playerGroups.deleteConfirm.description',
                "This won't affect existing sessions. You can undo this action.",
              )}
            </ResponsiveDialogDescription>
          </ResponsiveDialogHeader>
          <ResponsiveDialogFooter>
            <Button variant="outline" onClick={handleDeleteCancel} disabled={isDeleting}>
              {t('cancel')}
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                void handleDeleteConfirm();
              }}
              disabled={isDeleting}
            >
              {isDeleting
                ? t('playerGroups.deleting', 'Deleting...')
                : t('playerGroups.actions.delete', 'Delete')}
            </Button>
          </ResponsiveDialogFooter>
        </ResponsiveDialogContent>
      </ResponsiveDialog>
    </div>
  );
}
